// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {isNumber} from 'lodash';
import Tooltip from '@homeaway/react-tooltip';
import {UnitMessage as UrgencyMessage} from '@homeaway/react-urgency-messaging';
import Experiment from '@homeaway/react-experiment';
import GeoDistance from '../GeoDistance/GeoDistance';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {HA_SERP_FREE_CANCELLATION_V2} from '../../abacus/experiments';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';
import {FormattedMessage} from 'react-intl';
import HitLabel from '../HitLabel/HitLabel';
import TaxesAndFeesPaidAtProperty from './TaxesAndFeesPaidAtProperty';
import BadgeTooltip from './ReviewBadgeTooltip';

import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    exclusivityBadge: ?Badge,
    premierPartnerBadge: ?Badge
} & HitInfoProps;

type IconText = {
    icon: string,
    message: string,
    messageValueType: string
};

export type UnitMessage = {
    iconText: ?IconText
};

/**
 * This is the goofy, non-badge-looking PremierPartner label that typically
 * appears over the rating stars. Extracted locally to make
 * `showIncludesTaxesAndFeesMessage` logic easier to read.
 */
const PremierPartnerLabel = ({premierPartnerBadge, tooltipParentNode}) => (
    <Tooltip
        tooltipType="popover"
        content={premierPartnerBadge.helpText}
        placement="bottom"
        parentNode={tooltipParentNode}
        interactiveContainer
    >
        <span
            className="small"
            data-wdio="premier-partner-badge"
            tabIndex="-1"
            aria-hidden="true"
        >
            {premierPartnerBadge.name}
        </span>
    </Tooltip>
);

const hasSuperlative = (reviewBadges): %checks =>
    !!reviewBadges && !!reviewBadges.length;

/**
 * Classnames' flow-typed definition doesn't export an input type, so we're
 * using the deprecated existential type (*) to work around writing our own.
 */
type SuperlativeProps = {
    className?: *,
    reviewBadges: Badge[]
};
const Superlative = ({className, reviewBadges}: SuperlativeProps) => {
    if (!reviewBadges[0].helpText) {
        return (
            <span
                className={classNames('HitInfo__superlative small', className)}
                aria-hidden="true"
            >
                {reviewBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span
                className={classNames('HitInfo__superlative small', className)}
                aria-hidden="true"
            >
                {reviewBadges[0].name}
            </span>
            <BadgeTooltip helpText={reviewBadges[0].helpText} />
        </span>
    );
};

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    brand,
    premierPartnerBadge,
    exclusivityBadge,
    hitIndex,
    intl,
    isDesktop,
    isHovered,
    listing,
    isDatelessSearch,
    isMetric,
    fullLocale,
    showIncludesTaxesAndFeesMessage
}: Props) {
    let tooltipParentNode;
    const {
        freeCancellationUntil,
        instantBookable,
        propertyMetadata: {headline},
        reviewBadges,
        geoDistance,
        unitMessage
    } = listing;
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }

    return (
        <Fragment>
            <div className="media-flex__body">
                {
                    <UrgencyMessage
                        className="HitInfo__content--urgency"
                        size="s"
                        unitMessage={unitMessage}
                        ariaHidden
                    />
                }
                <h4 className="HitInfo__headline hover-text" aria-hidden="true">
                    {headline}
                </h4>

                {isNumber(hitIndex) && (
                    <span className="sr-only">
                        <FormattedMessage
                            id="property"
                            description="Property"
                            defaultMessage="Property"
                        >
                            {(txt) => (
                                <Fragment>
                                    {txt} {hitIndex + 1}
                                    {':'} {headline}
                                </Fragment>
                            )}
                        </FormattedMessage>
                    </span>
                )}

                <div className="HitInfo__details">
                    <Details listing={listing} isMetric={isMetric} />
                </div>

                {geoDistance && (
                    <GeoDistance
                        brand={brand}
                        geoDistance={geoDistance}
                        isHighlighted={isHovered}
                    />
                )}
                {/* TODO: Refactor to support displaying an array of badges */}
                {((showIncludesTaxesAndFeesMessage && premierPartnerBadge) ||
                    !!exclusivityBadge ||
                    freeCancellationUntil) && (
                    <div className="HitInfo__labels">
                        {showIncludesTaxesAndFeesMessage &&
                            premierPartnerBadge && (
                                <HitLabel
                                    brand={brand}
                                    id={premierPartnerBadge.id}
                                    label={premierPartnerBadge.name}
                                    parentNode={tooltipParentNode}
                                    toolTipText={premierPartnerBadge.helpText}
                                />
                            )}
                        {!!exclusivityBadge && (
                            <HitLabel
                                brand={brand}
                                id={exclusivityBadge.id}
                                label={exclusivityBadge.name}
                                parentNode={tooltipParentNode}
                                toolTipText={exclusivityBadge.helpText}
                            />
                        )}
                        {freeCancellationUntil && (
                            <Experiment
                                name={HA_SERP_FREE_CANCELLATION_V2}
                                variant={Experiment.ON}
                            >
                                <HitFreeCancellation
                                    isDesktop={isDesktop}
                                    freeCancellationUntil={
                                        freeCancellationUntil
                                    }
                                    formatMessage={intl.formatMessage}
                                    parentNode={tooltipParentNode}
                                />
                            </Experiment>
                        )}
                    </div>
                )}
            </div>
            <footer className="media-flex__footer hover-bg">
                <div className="HitInfo__priceSuperlativeGroup">
                    {!showIncludesTaxesAndFeesMessage &&
                        hasSuperlative(reviewBadges) && (
                            <Superlative reviewBadges={reviewBadges} />
                        )}
                    <div className="HitInfo__price">
                        <Price
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverageLabel={isDatelessSearch}
                            fullLocale={fullLocale}
                            instantBookable={instantBookable}
                            intl={intl}
                        />
                    </div>
                    {/* UK-only regulatory requirement: see mapStateToProps in HitInfo.js */}
                    {showIncludesTaxesAndFeesMessage && (
                        <div className="HitInfo__taxesAndFeesPaidAtProperty micro">
                            <TaxesAndFeesPaidAtProperty />
                        </div>
                    )}
                </div>
                <div className="HitInfo__badgeRatingGroup">
                    {showIncludesTaxesAndFeesMessage
                        ? hasSuperlative(reviewBadges) && (
                              <Superlative
                                  reviewBadges={reviewBadges}
                                  className={'text-right'}
                              />
                          )
                        : premierPartnerBadge && (
                              <PremierPartnerLabel
                                  premierPartnerBadge={premierPartnerBadge}
                                  tooltipParentNode={tooltipParentNode}
                              />
                          )}

                    <div className="HitInfo__rating">
                        <Rating
                            listing={filterGraphQlAnywhere(
                                ratingFragments.listing,
                                listing
                            )}
                        />
                    </div>
                </div>
            </footer>
        </Fragment>
    );
}
